import { computed, reactive, ref, toRefs, watch } from 'vue'
import { useStore } from '@freebe/sdk/store'
import debounce from 'lodash/debounce'

export default function useList (params) {
  const props = computed(() => Object.assign({
    skip: 0,
    limit: 10,
    loadMore: false,
    search: '',
    query: {},
    fuzzy: {},
    paginate: true,
    multiple: true,
    selection: [],
  }, params))

  if (!props.value.store) {
    throw new Error('Le namespace du store à utiliser doit impérativement être passé en option')
  }

  const { defaultId } = useStore(props.value.store)

  const idKey = ref(defaultId)

  const pagination = reactive({
    total: computed(() =>
      pagination.useLocal 
      ? Math.max(pagination.remoteTotal, pagination.localTotal)
      : pagination.remoteTotal 
    ),
    skip: computed(() => 
      // pagination.useLocal ? pagination.localSkip : pagination.remoteSkip
      pagination.remoteSkip
    ),
    limit: props.value.limit,
    remoteTotal: 0,
    remoteSkip: props.value.skip,
    localTotal: 0,
    localSkip: 0,
    useLocal: computed(() => !!state.search),
    next: computed(() => pagination.skip + pagination.limit),
    hasPrevious: computed(() => pagination.skip > 0), 
    hasNext: computed(() => pagination.skip + pagination.limit < pagination.total),
    fetchedIndexes: new Set(),
    loadMore: props.value.loadMore,
  })

  const state = reactive({
    search: props.value.search,
    selected: new Set(),
    error: '',
    loading: false,
    searching: false,
    allSelected: computed(() => {
      return !!items.value.length 
        && state.selected.size >= items.value.length 
        && !(items.value.some(item => !state.selected.has(item[idKey])))
    }),
  })

  let items = computed(() => {
    return find(props.value.store, {
      ...props.value.query,
      $fuzzy: props.value.fuzzy,
      $search: state.search,
      $skip: pagination.skip,
      $limit: pagination.limit,
      paginate: props.value.paginate && !pagination.loadMore,
    })
  })  

  watch(() => props.value.store, resetState, { immediate: false })
  watch(() => props.value.query, resetFetch, { immediate: false })
  watch(() => props.value.limit, resetLimit, { immediate: false })
  watch(() => pagination.limit, fetchItems, { immediate: false })
  watch(() => state.search, debounce(function (searchInput) {
    const { flush } = useStore(props.value.store)
    
    state.searching = !!searchInput 
    
    // flush()

    resetFetch()
  }, 100), { immediate: false })

  function find (store, query) {
    const Store = useStore(store)

    const results = Store.find(query) 
    
    if (results.data) {
      handleLocalPagination(results)
    }

    return results?.data || results
  }

  function fetch (query) {
    const Store = useStore(props.value.store)

    return Store.fetch(query)
  }

  function handleLocalPagination (results) {
    pagination.localTotal = results.total || 0
    pagination.localSkip = results.skip || 0
  }

  function handleRemotePagination (results) {
    if (pagination.remoteTotal !== results.total) {
      pagination.remoteTotal = results.total || 0
    }

    if (pagination.remoteSkip !== results.skip) {
      pagination.remoteSkip = results.skip || 0
    }

    if (results.limit && pagination.limit > results.limit) {
      pagination.limit = results.limit
    }
  }

  function resetState () {
    const { defaultId } = useStore(props.value.store)

    idKey.value = defaultId
    state.selected.clear()

    resetFetch()
  }

  function resetFetch () {
    pagination.fetchedIndexes.clear()

    goToPage(0)
  }

  function resetLimit (newLimit) {
    const forceFetch = newLimit > pagination.limit 

    pagination.limit = newLimit

    fetchItems(forceFetch)
  }

  async function fetchItems (force = false) {
    if (!force && pagination.fetchedIndexes.has(pagination.remoteSkip)) {
      state.searching = false 
      return 
    } else {
      pagination.fetchedIndexes.add(pagination.remoteSkip)
    }

    state.loading = true 

    try {
      const query = { ...props.value.query } 

      if (props.value.paginate) {
        query.$skip = pagination.remoteSkip
        query.$limit = pagination.limit
      }

      if (state.search && props.value.fuzzy.keys?.length) {
        const length = props.value.fuzzy.keys.length
        for (let i = length; i--;) {
          const key = props.value.fuzzy.keys[i]

          query[key] = { $search: state.search }
        }
      }

      const results = await fetch({
        query: query,
        paginate: props.value.paginate,
      })

      handleRemotePagination(results)
    } catch (error) {
      state.error = error.message || 'Erreur lors de la récupération des données'
    }

    state.loading = false
    state.searching = false 
  }

  function loadPrevious () {
    const previous = pagination.remoteSkip - pagination.limit

    if (pagination.hasPrevious) {
      pagination.remoteSkip = Math.max(0, previous)

      fetchItems()
    }
  }

  function loadNext () {
    if (pagination.hasNext) {
      pagination.remoteSkip += pagination.limit

      fetchItems()
    }
  }

  function goToPage (index) {
    pagination.remoteSkip = index * pagination.limit

    fetchItems()
  }

  function toggleSelection (id) {
    if (state.selected.has(id)) {
      state.selected.delete(id)
    } else {
      if (!props.value.multiple) {
        state.selected = new Set()
      } 

      state.selected.add(id)
    }

    return state.selected
  }

  function selectAll () {
    const length = items.value.length

    for (let i = 0; i < length; i++) {
      toggleSelection(items.value[i][idKey.value])
    }
  }

  fetchItems()

  for (let i = 0; i < props.value.selection.length; i++) {
    toggleSelection(props.value.selection[i])
  }

  return {
    idKey,
    items,
    ...toRefs(state),
    pagination,
    state,
    loadPrevious,
    loadNext,
    goToPage,
    toggleSelection,
    selectAll,
  }
}